import React, { useState, useEffect, Fragment } from "react";
import TipService from "../../../services/TipService";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import TipLoader from "./TipLoader";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DoneIcon from "@mui/icons-material/Done";
import {
  AWS_URL,
  REDUX_LOCAL_STORE_KEY,
} from "../../../components/config/constants";
import DisclaimerPaymentFooter from "../web/components/DisclaimerPaymentFooter";
import CcbillService from "../../../services/ccbillService";
import AddNewCardModalCcbill from "../../../components/common/commonModal/AddNewCardModalCcbill.jsx";

const TipModal = ({ show, onHide, id, tipArea, objId, userName }) => {
  const { t } = useTranslation();
  const [state, setState] = useState({
    tipList: [],
    tipValue: 0,
    doPayment: false,
    customValue: null,
    message: "",
    cardList: [],
    showCards: false,
    selectedCardIndex: 0,
    loading: false,
    taxAmount: 0,
    totalWithTax: 0,
    tokenCcbill: "",
    isAddCardModalOpen: false
  });

  // Update state helper function
  const updateState = (newState) => {
    setState(prevState => ({ ...prevState, ...newState }));
  };

  useEffect(() => {
    if (show) {
      getTipList();
      getTokenCcbill();
      getAddedCardListCcbill();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, id]);

  const handleAddCardModal = (val) => {
    updateState({ isAddCardModalOpen: val });
  };

  const getAddedCardListCcbill = () => {
    CcbillService.getAddedCardList()
      .then((res) => {
        if (!res?.data?.data) {
          updateState({ cardList: [] });
          return;
        }
        
        const myCardList = res.data.data.map((card) => ({
          ...card,
          id: card.paymentTokenId,
          brand: card.cardName,
          card: card
        }));
        
        updateState({ 
          cardList: myCardList,
          selectedCardId: myCardList.length > 0 ? myCardList[0].id : null
        });
      })
      .catch((err) => {
        console.error("Failed to load cards:", err?.response?.message || err);
        updateState({ cardList: [] });
      });
  };

  const getTokenCcbill = () => {
    CcbillService.ccbillToken()
      .then((res) => {
        updateState({ tokenCcbill: res.data.data.access_token });
      })
      .catch((err) => {
        console.error("Failed to get token:", err);
      });
  };

  const addCardCcbill = (myNewCardDetailsToEdit, extraDetails) => {
    updateState({ loading: true, isAddCardModalOpen: false });
    
    CcbillService.createPaymentToken(myNewCardDetailsToEdit, state.tokenCcbill)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Request failed");
        }
        return res.json();
      })
      .then((data) => {
        addCardToCcbill(data, extraDetails);
      })
      .catch((error) => {
        console.error("Error adding card:", error);
        updateState({ loading: false, isAddCardModalOpen: false });
        toast.error(t("payment.cardAddError"));
      });
  };

  const addCardToCcbill = async (myCard, cardDetails) => {
    const editedCardDetails = {
      ...cardDetails,
      paymentTokenId: myCard.paymentTokenId
    };
    
    CcbillService.ccbillAddCard(editedCardDetails)
      .then(() => {
        updateState({ loading: false, isAddCardModalOpen: false });
        getAddedCardListCcbill();
        toast.success(t("payment.cardAddedSuccess"));
      })
      .catch((err) => {
        console.error("Error saving card:", err);
        updateState({ loading: false });
        toast.error(t("payment.cardSaveError"));
      });
  };

  const totalTip = () => {
    updateState({ loading: true });
    
    const params = {
      userCardId: state.cardList[state.selectedCardIndex]?._id,
      customAmount: state.tipValue * 100,
    };

    TipService.totalTip(params)
      .then((res) => {
        if (res.status === 200) {
          updateState({
            loading: false,
            taxAmount: res.data.data.taxAmount / 100,
            totalWithTax: res.data.data.tipAmountIncludedTaxes / 100,
          });
        }
      })
      .catch((err) => {
        console.error("Error calculating total:", err);
        updateState({ loading: false });
        toast.error(t("payment.calculationError"));
      });
  };

  const sendTip = () => {
    onHide();
    updateState({ loading: true });
    
    const params = {
      creatorId: id,
      message: state.message,
      userCardId: state.cardList[state.selectedCardIndex]?._id,
      customAmount: state.tipValue * 100,
      tipArea: tipArea,
      objId: objId,
      ccbillOAuthToken: state.tokenCcbill,
    };

    TipService.sendTip(params)
      .then((res) => {
        if (res.status === 200) {
          updateState({ loading: false });
          toast.success(t("tip.tipSuccessfullySend"));
        }
      })
      .catch((err) => {
        updateState({ loading: false });
        toast.error(err.message || t("payment.tipSendError"));
      });
  };

  const getTipList = () => {
    updateState({ loading: true });
    
    TipService.tipList({ creatorId: id })
      .then((res) => {
        updateState({ tipList: res.data.data, loading: false });
      })
      .catch((err) => {
        updateState({ loading: false });
        toast.error(err.message || t("tip.listError"));
      });
  };

  const handleTipValue = (value) => {
    let sanitizedValue = value.replace(/[^0-9.,]/g, "");
    
    if (/^[0-9]*\.?[0-9]{0,2}$/.test(sanitizedValue)) {
      const numValue = parseFloat(sanitizedValue);
      
      if (sanitizedValue === "" || isNaN(numValue)) {
        updateState({
          tipValue: 0,
          customValue: null,
        });
      } else if (numValue <= 1000 && numValue >= 0) {
        updateState({
          tipValue: numValue,
          customValue: numValue,
        });
      } else {
        updateState({
          tipValue: 1000,
          customValue: 1000,
        });
      }
    }
  };

  // Destructure values from state for cleaner access
  const {
    tipList,
    loading,
    showCards,
    selectedCardIndex,
    tipValue,
    customValue,
    doPayment,
    message,
    cardList,
    taxAmount,
    totalWithTax,
    isAddCardModalOpen,
  } = state;

  const handleContinue = () => {
    if (tipValue >= 3) {
      updateState({ doPayment: true }, totalTip);
    } else {
      toast.error(t("tip.min1"));
    }
  };

  const handleSendTip = () => {
    if (cardList[selectedCardIndex]?.id) {
      sendTip();
    } else {
      toast.error(t("tip.noCard"));
    }
  };

  const handleCardSelect = (index) => {
    updateState({ 
      showCards: false,
      selectedCardIndex: index 
    }, totalTip);
  };

  return (
    <Fragment>
      <Modal
        show={show}
        onHide={onHide}
        keyboard={false}
        centered
        style={isAddCardModalOpen ? { opacity: "0" } : {}}
      >
        <Modal.Header closeButton className="ps-3 pt-2 pb-2">
          <span className="text-color-dark fw-700 font-xssss">
            {t("tip.tipCreator")}
          </span>
        </Modal.Header>
        
        {loading ? (
          <Modal.Body className="add-card-modal">
            <Loader />
            <TipLoader />
          </Modal.Body>
        ) : (
          <Modal.Body style={{ maxHeight: "70vh", overflowY: "scroll" }}>
            {/* Initial Tip Selection View */}
            {!doPayment && (
              <>
                <span className="f-black darktheme-txt wallet-secret-text">
                  {t("tip.tipAmount")}
                </span>

                <div style={{ flexWrap: "wrap" }} className="flex-paginator">
                  {/* Predefined tip amounts */}
                  {tipList.map((value, index) => (
                    <Button
                      key={value._id || index}
                      style={{ maxWidth: "25%" }}
                      onClick={() => updateState({ tipValue: value.price })}
                      className={`${
                        value.price === tipValue
                          ? "tip-button-selected"
                          : "tip-button"
                      } tip-margin-rigth ${
                        index === 3 && "remove-margin-rigth"
                      }`}
                    >
                      ${value.price}
                    </Button>
                  ))}
                  
                  {/* Custom amount input */}
                  {tipList.length < 4 ? (
                    <span
                      style={{
                        display: "flex",
                        width: "25%",
                        flexWrap: "wrap",
                      }}
                    >
                      <input
                        value={customValue || ""}
                        style={{ width: "calc(25% - 8px)" }}
                        onClick={(e) => handleTipValue(e.target.value)}
                        onChange={(e) => handleTipValue(e.target.value)}
                        type="number"
                        placeholder={t("tip.custom")}
                        className={`${
                          customValue === tipValue
                            ? "tip-button-selected"
                            : "tip-button"
                        } input-tip`}
                      />
                      <span
                        style={{
                          fontSize: "14px",
                          width: "0px",
                          color: customValue ? "#C80000" : "#A3A3A3",
                          marginBottom: "17px",
                          right: "92.5%",
                          alignSelf: "center",
                          position: "relative",
                          paddingRight: "2px",
                        }}
                      >
                        $
                      </span>
                      <div
                        style={{
                          textAlignLast: "left",
                          marginTop: "-6px",
                        }}
                        className="text-min-price-chat"
                      >
                        {t("chat.minPrice3")}
                      </div>
                    </span>
                  ) : (
                    <>
                      <span style={{ display: "flex" }}>
                        <input
                          value={customValue || ""}
                          style={{ maxWidth: "calc(25% - 8px)" }}
                          onClick={(e) => handleTipValue(e.target.value)}
                          onChange={(e) => handleTipValue(e.target.value)}
                          type="number"
                          placeholder={t("tip.custom")}
                          className={`${
                            customValue === tipValue
                              ? "tip-button-selected"
                              : "tip-button"
                          } input-tip`}
                        />
                        {customValue && (
                          <span
                            style={{
                              fontSize: "14px",
                              width: "0px",
                              color: customValue === tipValue ? "#C80000" : "#A3A3A3",
                              marginBottom: "16px",
                              right: "22%",
                              alignSelf: "center",
                              position: "relative",
                              paddingRight: "2px",
                            }}
                          >
                            $
                          </span>
                        )}
                      </span>
                      <div
                        style={{
                          textAlignLast: "left",
                          marginTop: "-6px",
                        }}
                        className="text-min-price-chat"
                      >
                        {t("chat.minPrice3")}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
            
            {/* Payment Review View */}
            {doPayment && (
              <>
                <p className="f-black darktheme-txt font-xsss">
                  {t("tip.descriptionModal")}
                </p>
                
                <span
                  style={{ marginBottom: "8px" }}
                  className="card-money-choose f-black darktheme-txt"
                >
                  {t("tip.tipAmount")}
                </span>
                <div className="card-money-available f-black darktheme-txt mb-16px">
                  ${tipValue}
                </div>
                
                {taxAmount > 0 && (
                  <>
                    <span
                      style={{ marginBottom: "8px" }}
                      className="card-money-choose f-black darktheme-txt"
                    >
                      {t("payment.tax")}
                    </span>
                    <div className="card-money-available f-black darktheme-txt mb-16px">
                      ${taxAmount}
                    </div>
                  </>
                )}
                
                <span
                  style={{ marginBottom: "8px" }}
                  className="card-money-choose f-black darktheme-txt"
                >
                  {t("common.total")}
                </span>
                <div className="card-money-available f-black darktheme-txt mb-16px">
                  ${totalWithTax}
                </div>
              </>
            )}

            {/* Message Input or Display */}
            {!doPayment ? (
              <>
                <span
                  style={{ marginBottom: "8px" }}
                  className="wallet-secret-text f-black darktheme-txt"
                >
                  {t("tip.optionalMessage")}
                </span>

                <div className="text-area-tip mb-16px">
                  <textarea
                    maxLength={150}
                    placeholder={t("tip.tipMessage")}
                    className="fix-text-area-dark-tip post-input no-resize"
                    onChange={(e) => updateState({ message: e.target.value })}
                  />
                </div>
              </>
            ) : (
              <>
                <span className="card-money-choose f-black darktheme-txt">
                  {t("tip.optionalMessage")}
                </span>
                <textarea
                  value={message}
                  disabled={true}
                  style={{ backgroundColor: "transparent" }}
                  placeholder={t("tip.noMessage")}
                  className={`post-input no-resize card-money-available ${
                    message.length < 50 && "height24px"
                  }`}
                  readOnly
                />
              </>
            )}
            
            {/* Payment Method Selection */}
            <div className="obliterate-padding-footer-modal">
              {doPayment && (
                <Modal.Footer className="payment-card">
                  <div className="text-payment-method-tip f-black darktheme-txt">
                    {t("tip.paymentMethod")}
                  </div>
                  
                  <div style={{ width: "100%" }} className="fix-style-payment-chat">
                    <div className="payment-page">
                      <div className="payment-card" style={{ border: "0px" }}>
                        <form action="#">
                          <div
                            className={`mb-3 ${
                              cardList.length !== 0 ? "payment-option cards" : ""
                            }`}
                          >
                            {cardList.length > 0 && (
                              <div className="checkout-option">
                                <input
                                  type="radio"
                                  className="cursor-pointer"
                                  id="Black"
                                  name="payment-method"
                                  checked={true}
                                  readOnly
                                />
                                <label className="ms-3 payment-details w-100 d-flex justify-content-between align-items-center">
                                  <div className="card-detail">
                                    <div className="card-logo">
                                      <img
                                        src={`${AWS_URL}logo/${
                                          cardList[selectedCardIndex]?.card?.logoImageName
                                        }`}
                                        alt="card-logo"
                                        height={44}
                                      />
                                    </div>
                                    <div className="card-number">
                                      <span className="subtitle text-color-dark">
                                        **** {cardList[selectedCardIndex]?.card?.last4}
                                      </span>
                                      <span className="details details-text fw-400 text-xssss">
                                        {t("payment.expiresOn")}{" "}
                                        {cardList[selectedCardIndex]?.card?.expMonth +
                                          "/" +
                                          cardList[selectedCardIndex]?.card?.expYear
                                            .toString()
                                            .slice(-2)}
                                      </span>
                                    </div>
                                  </div>
                                  <KeyboardArrowDownIcon
                                    onClick={() => updateState({ showCards: !showCards })}
                                    className="pointer"
                                  />
                                </label>
                              </div>
                            )}
                            
                            {showCards && (
                              <div className="all-cards-dropdown overflow-y-scroll">
                                {cardList.map((card, index) => (
                                  <div
                                    key={card.id || index}
                                    className={`card-datails-container pointer d-flex justify-content-between align-items-center ${
                                      selectedCardIndex === index ? "selected" : ""
                                    }`}
                                    onClick={() => handleCardSelect(index)}
                                  >
                                    <div className="card-detail">
                                      <div className="card-logo">
                                        <img
                                          src={`${AWS_URL}logo/${card?.card?.logoImageName}`}
                                          alt="card-logo"
                                          height={44}
                                        />
                                      </div>
                                      <div className="card-number">
                                        <span className="subtitle text-color-dark fw-bold text-xsss">
                                          {card.card.brand + "**** " + card.card.last4}
                                        </span>
                                        <span className="details details-text fw-400 text-xssss">
                                          {t("payment.expiresOn")}{" "}
                                          {card.card.expMonth +
                                            "/" +
                                            card.card.expYear.toString().slice(-2)}
                                        </span>
                                      </div>
                                    </div>
                                    {selectedCardIndex === index && (
                                      <DoneIcon className="selected-icon text-white bg-red rounded-pill p-1" />
                                    )}
                                  </div>
                                ))}

                                <div
                                  onClick={() => {
                                    updateState({ 
                                      showCards: false,
                                      isAddCardModalOpen: true 
                                    });
                                  }}
                                  className="card-datails-container pointer payment-details"
                                >
                                  + {t("payment.addNewCard")}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="mb-1 payment-option">
                            <div
                              className="checkout-option"
                              onClick={() => updateState({ isAddCardModalOpen: true })}
                            >
                              <label className="ms-3 payment-details darktheme-txt pointer">
                                + {t("payment.addNewCard")}
                              </label>
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </Modal.Footer>
              )}
            </div>
            
            {/* Footer Actions */}
            <div className="row">
              <div className="col-12">
                <Modal.Footer className="d-flex p-2 justify-content-end">
                  <div
                    className="pointer fw-bold me-4 font-xsss text-silver"
                    onClick={onHide}
                  >
                    {t("common.cancel")}
                  </div>

                  {!doPayment ? (
                    <Button
                      className="fix-tip-button fw-bold me-2 rounded-pill font-xssss text-white px-3 py-2 pointer"
                      onClick={handleContinue}
                    >
                      {t("tip.sendWithTip")}
                    </Button>
                  ) : (
                    <Button
                      className="fix-tip-button fw-bold me-2 rounded-pill font-xssss text-white px-3 py-2 pointer"
                      onClick={handleSendTip}
                      disabled={!cardList[selectedCardIndex]?.id}
                    >
                      {t("tip.sendWithTip")}
                    </Button>
                  )}

                  <div className="disclaimer darktheme-txt mb-2">
                    {t("tip.disclaimer", { userName })}
                  </div>
                  <DisclaimerPaymentFooter />
                </Modal.Footer>
              </div>
            </div>
          </Modal.Body>
        )}
      </Modal>
      
      <AddNewCardModalCcbill
        onSubmit={(myNewCardDetails, extraDetails) => 
          addCardCcbill(myNewCardDetails, extraDetails)
        }
        show={isAddCardModalOpen}
        handleModal={handleAddCardModal}
        subscribedUserId={
          JSON.parse(localStorage.getItem(REDUX_LOCAL_STORE_KEY))
            .authentication.userData._id
        }
        propsT={t}
      />
    </Fragment>
  );
};

export default TipModal;